import React, { useState, useRef } from 'react';
import { CheckCircle2, Upload, X, FileText } from 'lucide-react';
import { InquiryHero, PrivacyNote, FormActions, submitInquiry } from './formUtils';
import { Section, TextField, TextareaField, SelectField, MultiSelectField, FieldWrapper, ConditionalText, ConsentSection } from './FormFields';
import { BECOME_PARTNER_OPTIONS, MAX_FILE_SIZE_BYTES, COUNTRY_LIST } from '../../data/partnerOptions';
import { SEO } from '../../components/SEO';
import { SEO_CONTENT } from '../../data/seoContent';

// Accept extended file types for this form only (company docs, photos).
const PARTNER_ACCEPTED_TYPES = '.pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png';

const PartnerFileUpload = ({ files, onChange }) => {
  const inputRef = useRef(null);
  const [error, setError] = useState('');

  const handleAdd = (newFiles) => {
    setError('');
    const list = Array.from(newFiles || []);
    const valid = [];
    for (const f of list) {
      if (f.size > MAX_FILE_SIZE_BYTES) {
        setError(`"${f.name}" exceeds 25 MB limit.`);
        continue;
      }
      valid.push(f);
    }
    onChange([...(files || []), ...valid]);
  };

  const handleRemove = (idx) => onChange((files || []).filter((_, i) => i !== idx));

  return (
    <FieldWrapper
      label="Upload company documents"
      helper="Upload your company profile, capability brochure, quality certificates, or facility photos (PDF, DOC, PPT, JPG, PNG - max 25 MB per file)."
    >
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleAdd(e.dataTransfer.files); }}
        className="cursor-pointer border-2 border-dashed border-gray-300 rounded-md p-6 text-center hover:border-violet-500 hover:bg-violet-50/40 transition-colors"
        data-testid="partner-file-dropzone"
      >
        <Upload className="mx-auto text-gray-400 mb-2" size={24} />
        <p className="text-sm text-gray-700 font-medium">Drop files here or click to browse</p>
        <p className="text-xs text-gray-500 mt-1">PDF, DOC, PPT, JPG, PNG · Max 25 MB per file</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={PARTNER_ACCEPTED_TYPES}
          onChange={(e) => handleAdd(e.target.files)}
          className="hidden"
          data-testid="partner-file-input"
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {(files || []).length > 0 && (
        <ul className="space-y-1.5 mt-2">
          {files.map((f, idx) => (
            <li key={`${f.name}-${idx}`} className="flex items-center justify-between text-sm bg-gray-50 border border-gray-200 rounded px-3 py-1.5">
              <span className="flex items-center gap-2 text-gray-800 truncate">
                <FileText size={14} className="text-violet-600 flex-shrink-0" />
                <span className="truncate">{f.name}</span>
                <span className="text-xs text-gray-500 flex-shrink-0">({(f.size / 1024 / 1024).toFixed(2)} MB)</span>
              </span>
              <button type="button" onClick={() => handleRemove(idx)} className="text-gray-400 hover:text-red-600 transition-colors flex-shrink-0">
                <X size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </FieldWrapper>
  );
};

const PartnerSuccessBanner = () => (
  <div className="bg-violet-50 border-l-4 border-violet-600 rounded-md p-6 mb-8 flex items-start gap-4" data-testid="partner-success-banner">
    <CheckCircle2 className="text-violet-600 flex-shrink-0 mt-0.5" size={28} />
    <div>
      <h3 className="text-lg font-bold text-violet-900 mb-1">Thank you for your interest in joining LAMKO's partner network.</h3>
      <p className="text-sm text-violet-800 leading-relaxed">
        Our partnership team will review your application and contact you within 5 business days if your capabilities align with our current requirements.
      </p>
    </div>
  </div>
);

const initial = {
  // Company
  companyName: '', tradingName: '', countryIncorporation: '', countryIncorporationOther: '', countrySubsidiaries: [], website: '', yearEstablished: '',
  employeeCount: '', revenueRange: '',
  // Primary contact
  contactName: '', jobTitle: '', email: '', phone: '', preferredLanguage: '',
  // Partner type
  partnerType: '', partnerTypeOther: '',
  servicesOffered: [], servicesOfferedOther: '',
  // Industry / materials
  industrySectorsServed: [], materialClasses: [], materialClassesOther: '',
  // Technical
  chemistryCapabilities: [], chemistryCapabilitiesOther: '',
  reactorCapacity: '',
  purificationEquipment: [], purificationEquipmentOther: '',
  analyticalInstruments: [], analyticalInstrumentsOther: '',
  // Quality
  qualityCertifications: [], qualityCertificationsOther: '',
  facilityAudits: '', cleanroom: '', ehsCapabilities: [],
  // Capacity
  capacityUtilization: '', availability: '', facilityLocation: '', partnershipInterest: [],
  // Additional
  companyProfile: '',
  // Terms
  howHeard: '', ndaRequired: '',
  consentRequired: false, consentOptional: false,
};

export const BecomePartnerForm = () => {
  const [data, setData] = useState(initial);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showConsentError, setShowConsentError] = useState(false);

  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.consentRequired) { setShowConsentError(true); return; }
    setError('');
    setSubmitting(true);
    try {
      const sections = [
        { title: 'Company Information', fields: [
          { label: 'Company / legal entity name', value: data.companyName },
          { label: 'Trading name (if different)', value: data.tradingName },
          { label: 'Country of incorporation', value: data.countryIncorporation },
          { label: 'Country of incorporation - specified', value: data.countryIncorporationOther },
        { label: 'Country of subsidiaries/branches/incorporation/JVs', value: (data.countrySubsidiaries || []).join(', ') },
          { label: 'Company website', value: data.website },
          { label: 'Year established', value: data.yearEstablished },
          { label: 'Number of employees', value: data.employeeCount },
          { label: 'Annual revenue range', value: data.revenueRange },
        ]},
        { title: 'Primary Contact', fields: [
          { label: 'Contact person name', value: data.contactName },
          { label: 'Job title', value: data.jobTitle },
          { label: 'Business email', value: data.email },
          { label: 'Phone number', value: data.phone },
          { label: 'Preferred language', value: data.preferredLanguage },
        ]},
        { title: 'Partner Type & Services Offered', fields: [
          { label: 'Partner type', value: data.partnerType },
          { label: 'Partner type - specified', value: data.partnerTypeOther },
          { label: 'Services you can provide to LAMKO', value: data.servicesOffered },
          { label: 'Services - other specified', value: data.servicesOfferedOther },
        ]},
        { title: 'Industry Focus & Material Expertise', fields: [
          { label: 'Industry sectors you serve', value: data.industrySectorsServed },
          { label: 'Material classes you work with', value: data.materialClasses },
          { label: 'Material classes - other specified', value: data.materialClassesOther },
        ]},
        { title: 'Technical Capabilities', fields: [
          { label: 'Chemistry capabilities', value: data.chemistryCapabilities },
          { label: 'Chemistry - other specified', value: data.chemistryCapabilitiesOther },
          { label: 'Reactor / equipment capacity', value: data.reactorCapacity },
          { label: 'Purification equipment available', value: data.purificationEquipment },
          { label: 'Purification equipment - other specified', value: data.purificationEquipmentOther },
          { label: 'Analytical instruments available', value: data.analyticalInstruments },
          { label: 'Analytical instruments - other specified', value: data.analyticalInstrumentsOther },
        ]},
        { title: 'Quality & Compliance', fields: [
          { label: 'Quality certifications held', value: data.qualityCertifications },
          { label: 'Quality certifications - other specified', value: data.qualityCertificationsOther },
          { label: 'Facility audits', value: data.facilityAudits },
          { label: 'Cleanroom / controlled environment', value: data.cleanroom },
          { label: 'EHS capabilities', value: data.ehsCapabilities },
        ]},
        { title: 'Capacity & Availability', fields: [
          { label: 'Current capacity utilization', value: data.capacityUtilization },
          { label: 'Available for new projects starting', value: data.availability },
          { label: 'Facility location(s)', value: data.facilityLocation },
          { label: 'Interested in', value: data.partnershipInterest },
        ]},
        { title: 'Additional Information', fields: [
          { label: 'Company profile / capability summary', value: data.companyProfile },
        ]},
        { title: 'Terms', fields: [
          { label: 'How did you hear about LAMKO?', value: data.howHeard },
          { label: 'NDA required before sharing further details?', value: data.ndaRequired },
        ]},
        { title: 'Consent & Data Protection', fields: [
          { label: 'Required data-processing / authorization consent', value: data.consentRequired ? 'Yes - consent given' : 'No' },
          { label: 'Optional partnership/RFQ marketing consent', value: data.consentOptional ? 'Yes' : 'No' },
        ]},
      ];
      await submitInquiry({
        formType: 'become-partner',
        sections,
        meta: {
          fullName: data.contactName,
          companyName: data.companyName,
          businessEmail: data.email,
          country: data.countryIncorporation,
          partnerType: data.partnerType,
        },
        files,
      });
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.');
    }
    setSubmitting(false);
  };

  const reset = () => { setData(initial); setFiles([]); setError(''); setSuccess(false); };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO {...SEO_CONTENT.partnerBecome} />
      <InquiryHero
        title="Become a Partner"
        subtitle="CRO labs, manufacturing factories & purification facilities - join LAMKO's partner network"
      />
      <section className="py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
            {success && <PartnerSuccessBanner />}
            {error && <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm" data-testid="error-banner">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-8">
              <Section title="Company Information">
                <div className="grid sm:grid-cols-2 gap-5">
                  <TextField name="companyName" label="Company / legal entity name" required helper="Registered legal name of your organization." placeholder="e.g., ABC Co., Ltd." value={data.companyName} onChange={set('companyName')} />
                  <TextField name="tradingName" label="Trading name (if different)" helper="Brand or trading name if different from legal name." value={data.tradingName} onChange={set('tradingName')} />
                </div>
                <SelectField name="countryIncorporation" label="Country of incorporation" required helper="Where is your company legally registered?" options={BECOME_PARTNER_OPTIONS.countryIncorporation} value={data.countryIncorporation} onChange={set('countryIncorporation')} />
                <ConditionalText show={data.countryIncorporation === 'Other (please specify)'} name="countryIncorporationOther" placeholder="Please specify country of incorporation" value={data.countryIncorporationOther} onChange={set('countryIncorporationOther')} />
              <MultiSelectField name="countrySubsidiaries" label="Country of subsidiaries/branches/incorporation/JVs" helper="Select all countries where you have active subsidiaries, branches, or joint ventures." options={COUNTRY_LIST} values={data.countrySubsidiaries} onChange={set('countrySubsidiaries')} columns={3} />
                <div className="grid sm:grid-cols-2 gap-5">
                  <TextField name="website" label="Company website" helper="Your company URL." placeholder="e.g., https://www.company.com" value={data.website} onChange={set('website')} />
                  <TextField name="yearEstablished" label="Year established" helper="When was the company founded?" placeholder="e.g., 2005" value={data.yearEstablished} onChange={set('yearEstablished')} />
                  <SelectField name="employeeCount" label="Number of employees" helper="Total headcount across all locations." options={BECOME_PARTNER_OPTIONS.employeeCount} value={data.employeeCount} onChange={set('employeeCount')} />
                  <SelectField name="revenueRange" label="Annual revenue range" helper="Approximate annual revenue (USD equivalent)." options={BECOME_PARTNER_OPTIONS.revenueRange} value={data.revenueRange} onChange={set('revenueRange')} />
                </div>
              </Section>

              <Section title="Primary Contact">
                <div className="grid sm:grid-cols-2 gap-5">
                  <TextField name="contactName" label="Contact person name" required helper="Primary point of contact for this partnership." placeholder="e.g., Dr. Min-Jun Park" value={data.contactName} onChange={set('contactName')} />
                  <TextField name="jobTitle" label="Job title" required helper="Position or role." placeholder="e.g., Business Development Manager" value={data.jobTitle} onChange={set('jobTitle')} />
                  <TextField name="email" type="email" label="Business email" required helper="Primary email for partnership communications." placeholder="e.g., contact@company.com" value={data.email} onChange={set('email')} />
                  <TextField name="phone" type="tel" label="Phone number" required helper="Include country code." placeholder="e.g., +82-10-1234-5678" value={data.phone} onChange={set('phone')} />
                </div>
                <SelectField name="preferredLanguage" label="Preferred language" helper="For communications." options={BECOME_PARTNER_OPTIONS.preferredLanguage} value={data.preferredLanguage} onChange={set('preferredLanguage')} />
              </Section>

              <Section title="Partner Type & Services Offered">
                <SelectField name="partnerType" label="Partner type" required helper="Select the category that best describes your organization." options={BECOME_PARTNER_OPTIONS.partnerType} value={data.partnerType} onChange={set('partnerType')} />
                <ConditionalText show={data.partnerType === 'Multiple types (describe in notes below)'} name="partnerTypeOther" helper="Please describe the multiple types / combination." placeholder="e.g., CRO + analytical lab + small-scale CMO" value={data.partnerTypeOther} onChange={set('partnerTypeOther')} />
                <MultiSelectField name="servicesOffered" label="Services you can provide to LAMKO" required helper="Select all services your organization can offer." options={BECOME_PARTNER_OPTIONS.servicesOffered} values={data.servicesOffered} onChange={set('servicesOffered')} columns={2} />
                <ConditionalText show={(data.servicesOffered || []).includes('Other (specify in notes)')} name="servicesOfferedOther" helper="Please describe the other services you offer." placeholder="e.g., Custom spectroscopic calibration" value={data.servicesOfferedOther} onChange={set('servicesOfferedOther')} />
              </Section>

              <Section title="Industry Focus & Material Expertise">
                <MultiSelectField name="industrySectorsServed" label="Industry sectors you serve" required helper="Select all industries where you have active experience." options={BECOME_PARTNER_OPTIONS.industrySectorsServed} values={data.industrySectorsServed} onChange={set('industrySectorsServed')} columns={2} />
                <MultiSelectField name="materialClasses" label="Material classes you work with" required helper="Select all material types you have experience handling or producing." options={BECOME_PARTNER_OPTIONS.materialClasses} values={data.materialClasses} onChange={set('materialClasses')} columns={2} />
                <ConditionalText show={(data.materialClasses || []).includes('Other (specify in notes)')} name="materialClassesOther" helper="Please describe the other material classes." placeholder="e.g., Ionic liquids" value={data.materialClassesOther} onChange={set('materialClassesOther')} />
              </Section>

              <Section title="Technical Capabilities">
                <MultiSelectField name="chemistryCapabilities" label="Chemistry capabilities" helper="Select the reaction types your facility can perform." options={BECOME_PARTNER_OPTIONS.chemistryCapabilities} values={data.chemistryCapabilities} onChange={set('chemistryCapabilities')} columns={2} />
                <ConditionalText show={(data.chemistryCapabilities || []).includes('Other (specify in notes)')} name="chemistryCapabilitiesOther" helper="Please describe the other chemistry capabilities." placeholder="e.g., Flow chemistry / continuous reactors" value={data.chemistryCapabilitiesOther} onChange={set('chemistryCapabilitiesOther')} />
                <SelectField name="reactorCapacity" label="Reactor / equipment capacity" helper="Largest single reactor or processing vessel." options={BECOME_PARTNER_OPTIONS.reactorCapacity} value={data.reactorCapacity} onChange={set('reactorCapacity')} />
                <MultiSelectField name="purificationEquipment" label="Purification equipment available" helper="Select the purification equipment at your facility." options={BECOME_PARTNER_OPTIONS.purificationEquipment} values={data.purificationEquipment} onChange={set('purificationEquipment')} columns={2} />
                <ConditionalText show={(data.purificationEquipment || []).includes('Other (specify in notes)')} name="purificationEquipmentOther" helper="Please describe the other purification equipment." placeholder="" value={data.purificationEquipmentOther} onChange={set('purificationEquipmentOther')} />
                <MultiSelectField name="analyticalInstruments" label="Analytical instruments available" helper="Select all analytical equipment at your facility." options={BECOME_PARTNER_OPTIONS.analyticalInstruments} values={data.analyticalInstruments} onChange={set('analyticalInstruments')} columns={2} />
                <ConditionalText show={(data.analyticalInstruments || []).includes('Other (specify in notes)')} name="analyticalInstrumentsOther" helper="Please describe the other analytical instruments (including NMR MHz if applicable)." placeholder="" value={data.analyticalInstrumentsOther} onChange={set('analyticalInstrumentsOther')} />
              </Section>

              <Section title="Quality & Compliance">
                <MultiSelectField name="qualityCertifications" label="Quality certifications held" required helper="Select all current certifications." options={BECOME_PARTNER_OPTIONS.qualityCertifications} values={data.qualityCertifications} onChange={set('qualityCertifications')} columns={2} />
                <ConditionalText show={(data.qualityCertifications || []).includes('In process of obtaining (specify in notes)') || (data.qualityCertifications || []).includes('Other (specify in notes)')} name="qualityCertificationsOther" helper="Please specify the certification(s) - either in process or other." placeholder="" value={data.qualityCertificationsOther} onChange={set('qualityCertificationsOther')} />
                <SelectField name="facilityAudits" label="Facility audits" helper="Has your facility been audited by external customers or third parties?" options={BECOME_PARTNER_OPTIONS.facilityAudits} value={data.facilityAudits} onChange={set('facilityAudits')} />
                <SelectField name="cleanroom" label="Cleanroom / controlled environment" helper="Cleanroom capabilities at your facility." options={BECOME_PARTNER_OPTIONS.cleanroom} value={data.cleanroom} onChange={set('cleanroom')} />
                <MultiSelectField name="ehsCapabilities" label="EHS capabilities" helper="Environmental, health & safety capabilities." options={BECOME_PARTNER_OPTIONS.ehsCapabilities} values={data.ehsCapabilities} onChange={set('ehsCapabilities')} columns={2} />
              </Section>

              <Section title="Capacity & Availability">
                <SelectField name="capacityUtilization" label="Current capacity utilization" helper="How much of your production capacity is currently in use?" options={BECOME_PARTNER_OPTIONS.capacityUtilization} value={data.capacityUtilization} onChange={set('capacityUtilization')} />
                <SelectField name="availability" label="Available for new projects starting" required helper="When could you begin work on a new LAMKO project?" options={BECOME_PARTNER_OPTIONS.availability} value={data.availability} onChange={set('availability')} />
                <TextField name="facilityLocation" label="Facility location(s)" helper="City and country of your production / lab facility." placeholder="e.g., Suwon, South Korea" value={data.facilityLocation} onChange={set('facilityLocation')} />
                <MultiSelectField name="partnershipInterest" label="Interested in" required helper="What type of partnership arrangement are you open to?" options={BECOME_PARTNER_OPTIONS.partnershipInterest} values={data.partnershipInterest} onChange={set('partnershipInterest')} columns={2} />
              </Section>

              <Section title="Additional Information">
                <TextareaField name="companyProfile" label="Company profile / capability summary" helper="Briefly describe your core strengths, key differentiators, notable clients (if shareable), and any specific experience with electronics-grade or OLED materials." placeholder="e.g., We are a 50-person CRO based in Daejeon, South Korea with 15 years of experience in custom synthesis of heterocyclic and organometallic compounds for display applications. Our key strength is multi-step synthesis with Pd-catalyzed coupling reactions at kg scale with > 99.5% HPLC purity..." rows={6} value={data.companyProfile} onChange={set('companyProfile')} />
              </Section>

              <Section title="Document Upload">
                <PartnerFileUpload files={files} onChange={setFiles} />
              </Section>

              <Section title="Terms">
                <SelectField name="howHeard" label="How did you hear about LAMKO?" helper="This helps us understand our reach." options={BECOME_PARTNER_OPTIONS.howHeard} value={data.howHeard} onChange={set('howHeard')} />
                <SelectField name="ndaRequired" label="NDA required before sharing further details?" helper="Let us know if you need a confidentiality agreement." options={BECOME_PARTNER_OPTIONS.ndaRequired} value={data.ndaRequired} onChange={set('ndaRequired')} />
              </Section>

              <FormActions onReset={reset} isSubmitting={submitting} />
              <ConsentSection variant="partner" consent1={data.consentRequired} consent2={data.consentOptional} onChange1={set('consentRequired')} onChange2={set('consentOptional')} showError={showConsentError} />
              <PrivacyNote />
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};
